import { useEffect, useMemo, useState } from "react";

function diffParts(target) {
  const ms = Math.max(0, new Date(target).getTime() - Date.now());
  const totalSeconds = Math.floor(ms / 1000);
  return {
    expired: ms <= 0,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

const pad = (n) => String(n).padStart(2, "0");

/**
 * Countdown to a real deadline supplied by the backend.
 * Shows a days block automatically when the deadline is more than a day away.
 */
export const Countdown = ({
  target,
  label,
  testId = "countdown",
  variant = "default",
  expiredText = "Closed",
}) => {
  const [parts, setParts] = useState(() => diffParts(target));

  useEffect(() => {
    setParts(diffParts(target));
    const id = setInterval(() => setParts(diffParts(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  const blocks = useMemo(() => {
    const list = [];
    if (parts.days > 0) list.push({ v: String(parts.days), u: "d" });
    list.push({ v: pad(parts.hours), u: "h" });
    list.push({ v: pad(parts.minutes), u: "m" });
    list.push({ v: pad(parts.seconds), u: "s" });
    return list;
  }, [parts]);

  if (parts.expired) {
    return (
      <span
        className="text-xs font-semibold"
        data-testid={`${testId}-expired`}
        style={{ color: "var(--gm-muted-2)" }}
      >
        {expiredText}
      </span>
    );
  }

  const compact = variant === "compact";

  return (
    <div
      className={`flex items-center ${compact ? "gap-1" : "gap-1.5"}`}
      data-testid={testId}
      aria-label={label ? `${label} countdown` : "countdown"}
    >
      {blocks.map((b, i) => (
        <div key={b.u} className={`flex items-center ${compact ? "gap-1" : "gap-1.5"}`}>
          <span
            className={`font-mono-gm rounded-md font-semibold text-white ${
              compact ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-1 text-sm"
            }`}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          >
            {b.v}
            <span
              className={compact ? "ml-0.5 text-[9px]" : "ml-0.5 text-[10px]"}
              style={{ color: "var(--gm-muted-2)" }}
            >
              {b.u}
            </span>
          </span>
          {i < blocks.length - 1 && !compact ? (
            <span aria-hidden="true" style={{ color: "var(--gm-red)" }}>
              :
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
};
